import { Button } from '@strata/react-buttons';
import { Dialog } from '@strata/react-dialog';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

export const ApproveInternallyDialog = ({
    isOpen,
    fetchPolicy,
    onSecondaryButtonClick,
    statusHistory = undefined,
    payerId,
    draftId,
}) => {
    const payerDetails = useGet(`/reference/payers/${payerId}`);

    return (
        <Formik
            initialValues={{
                comment: '',
                sendForReview: false,
                sendToPendProd: false,
                // Set reviewer to last external change requestor if there is one
                reviewer: statusHistory?.find(
                    (change) =>
                        change.policy_status === ClinicalPolicyStatusIndicator.Statuses.EXTCHANGE,
                )?.username,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                reviewer: Yup.string().email('Must be a valid email'),
                comment: Yup.string(),
                sendForReview: Yup.bool().required(),
            })}
            onSubmit={async (
                { reviewer, comment, sendForReview, sendToPendProd },
                { resetForm },
            ) => {
                try {
                    await put(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}/approve`, {
                        internal: true,
                        comment,
                    });

                    if (sendForReview) {
                        try {
                            // Send for review if Approve & Send For External Review clicked
                            await put(
                                `/clinicalpolicy/admin/${payerId}/drafts/${draftId}/for_review`,
                                {
                                    policy_status: ClinicalPolicyStatusIndicator.Statuses.EXTREVIEW,
                                    reviewer,
                                    comment,
                                },
                            );
                        } catch (err) {
                            handleError('Error submitting external review request.', err);
                        }
                    }

                    if (sendToPendProd) {
                        try {
                            // Send to PEND_PROD if Approve & Send To Pending Production clicked
                            await put(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}/next`, {
                                policy_status: ClinicalPolicyStatusIndicator.Statuses.PEND_PROD,
                                comment,
                            });
                        } catch (err) {
                            handleError('Error sending to Pending Production.', err);
                        }
                    }

                    await fetchPolicy();
                    resetForm();
                    onSecondaryButtonClick();
                } catch (err) {
                    handleError('Error approving policy.', err);
                }
            }}
        >
            {(formik) => (
                <Dialog
                    dialogTitle="Approve Internally"
                    width={1000}
                    isOpen={isOpen}
                    primaryButtonLabel="Approve Internally"
                    onPrimaryButtonClick={() => {
                        formik.setValues({
                            ...formik.values,
                            sendForReview: false,
                            sendToPendProd: false,
                        });
                        formik.handleSubmit();
                    }}
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    additionalButtons={
                        <>
                            <Button
                                intent={Button.Intent.PRIMARY}
                                label="Approve &amp; Send To Pending Production"
                                onClick={() => {
                                    formik.setValues({
                                        ...formik.values,
                                        sendForReview: false,
                                        sendToPendProd: true,
                                    });
                                    formik.handleSubmit();
                                }}
                                disabled={!formik.isValid || formik.isSubmitting}
                            />
                            <Button
                                intent={Button.Intent.PRIMARY}
                                label="Approve &amp; Send For External Review"
                                onClick={() => {
                                    formik.setValues({
                                        ...formik.values,
                                        sendForReview: true,
                                        sendToPendProd: false,
                                    });
                                    formik.handleSubmit();
                                }}
                                disabled={
                                    !(formik.isValid && formik.values.reviewer) ||
                                    formik.isSubmitting
                                }
                            />
                        </>
                    }
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onSecondaryButtonClick();
                    }}
                    dialogContent={
                        <Form autoComplete="off">
                            <p>
                                Approving this policy internally will make it ready to be sent for
                                external review.
                            </p>
                            <p>
                                Choosing to approve and send for external review will send this
                                policy to {payerDetails?.payer_name} and they will be notified to
                                review the policy.
                            </p>
                            <p className="margin__bottom--large">
                                Choosing to approve and send to Pending Production will send this
                                policy directly to Pending Production.
                            </p>
                            <TextInputFormik id="reviewer" label="Reviewer Email" />
                            <TextAreaFormik id="comment" label="Comment" />
                        </Form>
                    }
                />
            )}
        </Formik>
    );
};

ApproveInternallyDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    fetchPolicy: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    payerId: PropTypes.string.isRequired,
    draftId: PropTypes.string.isRequired,
};
